import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { CustomerBasicInfo } from "@redotech/effect-api-shared/models/customer";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";
import { useCustomerSelector } from "../hooks/use-customer-selector";

export const CustomerSelector = memo(function CustomerSelector({
  conversationId,
  customer,
}: {
  conversationId: ConversationId;
  customer: CustomerBasicInfo | undefined;
}) {
  const {
    customerListItems,
    dropdownOpen,
    badgeRef,
    setBadgeRef,
    handleBadgeClick,
    handleDropdownOpenChange,
    handleItemSelected,
    searchText,
    setSearchText,
  } = useCustomerSelector({ conversationId, currentCustomer: customer });

  const profileImageUrl = useMemo(() => {
    if (!customer?.email) {
      return null;
    }
    return getUserAvatarUrl({
      email: customer.email,
      defaultImage: "404",
      size: Math.ceil(devicePixelRatio * 32),
    });
  }, [customer?.email]);

  return (
    <Flex cursor="pointer" onClick={handleBadgeClick} ref={setBadgeRef}>
      <RedoBadge
        avatar={{
          alt: "Customer profile picture",
          name: customer?.displayName,
          imageUrl: profileImageUrl,
        }}
        color="gray"
        size="sm"
        text={customer?.displayName || "Unknown"}
      />
      <RedoListDropdown
        dropdownAnchor={badgeRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor={false}
        items={customerListItems}
        itemSelected={({ item }) => handleItemSelected(item.value)}
        searchPlaceholder="Search customers..."
        searchString={searchText}
        selectionVariant="none"
        setDropdownOpen={handleDropdownOpenChange}
        setSearchString={setSearchText}
        size="xs"
      />
    </Flex>
  );
});
